/**
 * High-Performance Circular Buffer Implementation
 * 
 * Provides memory-efficient circular buffer for streaming data collection
 * with overflow handling and concurrent access support.
 */

import { Logger } from '@/utils/logger'

const logger = new Logger('CircularBuffer')

export interface CircularBufferConfig {
  maxSize: number
  overflowStrategy: 'overwrite' | 'drop' | 'expand'
  shrinkThreshold?: number // When to shrink buffer (as percentage of maxSize)
}

export interface BufferMetrics {
  size: number
  capacity: number
  overflows: number
  writes: number
  reads: number
  memoryUsageBytes: number
}

export class CircularBuffer<T> {
  private buffer: (T | undefined)[]
  private head = 0 // Next write position
  private tail = 0 // Next read position
  private count = 0 // Current number of items
  private readonly maxSize: number
  private readonly overflowStrategy: 'overwrite' | 'drop' | 'expand'
  private readonly shrinkThreshold: number

  // Metrics
  private overflows = 0
  private writes = 0
  private reads = 0

  constructor(config: CircularBufferConfig) {
    this.maxSize = config.maxSize
    this.overflowStrategy = config.overflowStrategy
    this.shrinkThreshold = config.shrinkThreshold ?? 0.25
    this.buffer = new Array(this.maxSize)
    
    logger.debug(`Created circular buffer`, {
      maxSize: this.maxSize,
      strategy: this.overflowStrategy
    })
  }

  /**
   * Add item to buffer
   */
  write(item: T): boolean {
    this.writes++

    // For expandable buffers, check actual buffer capacity
    // For non-expandable buffers, use maxSize
    const effectiveCapacity = this.overflowStrategy === 'expand' ? this.buffer.length : this.maxSize

    if (this.count < effectiveCapacity) {
      // Buffer has space
      this.buffer[this.head] = item
      this.head = (this.head + 1) % this.buffer.length
      this.count++
      return true
    }

    // Buffer is full - handle overflow
    return this.handleOverflow(item)
  }

  /**
   * Read next item from buffer
   */
  read(): T | undefined {
    if (this.count === 0) {
      return undefined
    }

    this.reads++
    const item = this.buffer[this.tail]
    this.buffer[this.tail] = undefined // Help GC
    this.tail = (this.tail + 1) % this.buffer.length
    this.count--

    // Check if we should shrink
    this.maybeShrink()

    return item
  }

  /**
   * Read multiple items at once
   */
  readBatch(size: number): T[] {
    const items: T[] = []
    const actualSize = Math.min(size, this.count)

    for (let i = 0; i < actualSize; i++) {
      const item = this.read()
      if (item !== undefined) {
        items.push(item)
      }
    }

    return items
  }

  /**
   * Peek at next item without removing it
   */
  peek(): T | undefined {
    if (this.count === 0) {
      return undefined
    }
    return this.buffer[this.tail]
  }

  /**
   * Clear all items from buffer
   */
  clear(): void {
    // Help GC by clearing references
    for (let i = 0; i < this.buffer.length; i++) {
      this.buffer[i] = undefined
    }
    
    this.head = 0
    this.tail = 0
    this.count = 0
    
    logger.debug('Buffer cleared')
  }

  /**
   * Get current size
   */
  size(): number {
    return this.count
  }

  /**
   * Get buffer capacity
   */
  capacity(): number {
    return this.buffer.length
  }

  /**
   * Check if buffer is empty
   */
  isEmpty(): boolean {
    return this.count === 0
  }

  /**
   * Check if buffer is full
   */
  isFull(): boolean {
    const effectiveCapacity = this.overflowStrategy === 'expand' ? this.buffer.length : this.maxSize
    return this.count >= effectiveCapacity
  }

  /**
   * Get all items as array (for debugging/testing)
   */
  toArray(): T[] {
    const items: T[] = []
    let current = this.tail
    
    for (let i = 0; i < this.count; i++) {
      const item = this.buffer[current]
      if (item !== undefined) {
        items.push(item)
      }
      current = (current + 1) % this.buffer.length
    }
    
    return items
  }

  /**
   * Get buffer metrics
   */
  getMetrics(): BufferMetrics {
    return {
      size: this.count,
      capacity: this.buffer.length,
      overflows: this.overflows,
      writes: this.writes,
      reads: this.reads,
      memoryUsageBytes: this.estimateMemoryUsage()
    }
  }

  /**
   * Handle buffer overflow based on strategy
   */
  private handleOverflow(item: T): boolean {
    this.overflows++

    switch (this.overflowStrategy) {
      case 'overwrite':
        // Overwrite oldest item
        this.buffer[this.head] = item
        this.head = (this.head + 1) % this.buffer.length
        this.tail = (this.tail + 1) % this.buffer.length
        logger.debug('Buffer overflow: overwriting oldest item')
        return true

      case 'drop':
        // Drop new item
        logger.debug('Buffer overflow: dropping new item')
        return false

      case 'expand':
        // Expand buffer (up to 2x original size)
        if (this.buffer.length < this.maxSize * 2) {
          this.expandBuffer()
          return this.write(item) // Retry after expansion
        } else {
          // Fallback to overwrite if can't expand further
          logger.warn('Buffer at maximum expansion, falling back to overwrite')
          this.buffer[this.head] = item
          this.head = (this.head + 1) % this.buffer.length
          this.tail = (this.tail + 1) % this.buffer.length
          return true
        }

      default:
        logger.error(`Unknown overflow strategy: ${this.overflowStrategy}`)
        return false
    }
  }

  /**
   * Expand buffer capacity
   */
  private expandBuffer(): void {
    const oldSize = this.buffer.length
    const newSize = Math.min(oldSize * 2, this.maxSize * 2)
    
    logger.debug(`Expanding buffer from ${oldSize} to ${newSize}`)

    // Create new buffer and copy items in order
    const newBuffer = new Array(newSize)
    const items = this.toArray()
    
    // Copy items to new buffer
    for (let i = 0; i < items.length; i++) {
      newBuffer[i] = items[i]
    }

    // Update pointers
    this.buffer = newBuffer
    this.head = items.length
    this.tail = 0
  }

  /**
   * Shrink buffer if usage is low
   */
  private maybeShrink(): void {
    if (this.overflowStrategy !== 'expand') {
      return // Only shrink expandable buffers
    }

    const usageRatio = this.count / this.buffer.length
    const minSize = this.maxSize
    
    if (this.buffer.length > minSize && usageRatio < this.shrinkThreshold) {
      this.shrinkBuffer()
    }
  }

  /**
   * Shrink buffer to remove unused capacity
   */
  private shrinkBuffer(): void {
    const oldSize = this.buffer.length
    const newSize = Math.max(this.maxSize, Math.ceil(this.count * 1.5))
    
    if (newSize >= oldSize) {
      return // No point shrinking
    }

    logger.debug(`Shrinking buffer from ${oldSize} to ${newSize}`)

    // Create new buffer and copy items
    const newBuffer = new Array(newSize)
    const items = this.toArray()
    
    for (let i = 0; i < items.length; i++) {
      newBuffer[i] = items[i]
    }

    // Update pointers
    this.buffer = newBuffer
    this.head = items.length
    this.tail = 0
  }

  /**
   * Estimate memory usage of the buffer
   */
  private estimateMemoryUsage(): number {
    // Base array overhead
    let bytes = this.buffer.length * 8 // Array slot overhead
    
    // Add estimated size of items
    for (let i = 0; i < this.buffer.length; i++) {
      const item = this.buffer[i]
      if (item !== undefined) {
        bytes += this.estimateItemSize(item)
      }
    }

    return bytes
  }

  /**
   * Estimate size of a single item
   */
  private estimateItemSize(item: any): number {
    if (item === null || item === undefined) {
      return 8
    }

    switch (typeof item) {
      case 'string':
        return item.length * 2 + 40 // UTF-16 + object overhead
      case 'number':
        return 16 // Number object
      case 'boolean':
        return 12 // Boolean object
      case 'object':
        return this.estimateObjectSize(item)
      default:
        return 16 // Default object overhead
    }
  }

  /**
   * Estimate size of an object (simplified)
   */
  private estimateObjectSize(obj: any): number {
    if (Array.isArray(obj)) {
      return obj.length * 8 + 40 // Array overhead
    }
    
    // Object overhead + estimated property count
    return Object.keys(obj).length * 40 + 40
  }
}